import type { ThemeTokens } from '../tokens'
import { signalDark } from './signal-dark'
import { signalLight } from './signal-light'

/**
 * One look, Signal, in dark and light. The values are generated from design/tokens.json by
 * scripts/gen-design-tokens.mjs, which also themes the Support app and the docs, so every surface
 * of the product shares one palette and one typeface.
 *
 * Earlier releases offered two styles (Technical and Professional) on a second axis. A preference
 * saved then is carried over by its mode: see `migrateStoredTheme`.
 */
export const THEME_MODES = ['dark', 'light'] as const

export type ThemeMode = (typeof THEME_MODES)[number]
export type ThemeId = `signal-${ThemeMode}`

export interface ThemeInfo {
  id: ThemeId
  mode: ThemeMode
  modeName: string
}

/** Used when nothing is stored and the system expresses no preference. */
export const DEFAULT_THEME: ThemeId = 'signal-dark'

/** Where an explicit choice is persisted. Unset means "match the system". */
export const THEME_STORAGE_KEY = 'relay-theme'

export const THEMES: Record<ThemeId, ThemeInfo> = {
  'signal-dark': { id: 'signal-dark', mode: 'dark', modeName: 'Dark' },
  'signal-light': { id: 'signal-light', mode: 'light', modeName: 'Light' },
}

/** The token values behind each theme. `themes.css` is asserted against this map. */
export const THEME_TOKENS: Record<ThemeId, ThemeTokens> = {
  'signal-dark': signalDark,
  'signal-light': signalLight,
}

export const THEME_IDS = Object.keys(THEME_TOKENS) as ThemeId[]

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(THEME_TOKENS, value)
}

export function themeForMode(mode: ThemeMode): ThemeId {
  return `signal-${mode}`
}

/** The same theme in the other mode — what a dark/light toggle does. */
export function toggledMode(theme: ThemeId): ThemeId {
  return themeForMode(THEMES[theme].mode === 'dark' ? 'light' : 'dark')
}

/**
 * A stored preference, read forgivingly. A current id is returned as is; one an earlier release
 * wrote (`technical-light`, `professional-dark`) keeps its mode, so nobody's light/dark choice is
 * lost with the old styles. Anything else is no preference at all.
 */
export function migrateStoredTheme(value: unknown): ThemeId | null {
  if (isThemeId(value)) return value
  if (typeof value !== 'string') return null
  const mode = /-(dark|light)$/.exec(value)?.[1] as ThemeMode | undefined
  return mode ? themeForMode(mode) : null
}

export { signalDark, signalLight }
